import { useState, useRef } from 'react';
import { Modal, ModalBody, ModalFooter, Button, Alert, Spinner } from '@components/ui';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { productService } from '../services/productService';
import type { ProductImportError } from '../types/product.types';

interface ProductImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ProductImportModal({ isOpen, onClose, onSuccess }: ProductImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [errors, setErrors] = useState<ProductImportError[]>([]);
  const [generalError, setGeneralError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await productService.downloadTemplate();
    } catch (error) {
      setGeneralError('Error al descargar la plantilla');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setErrors([]);
      setGeneralError('');
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setIsLoading(true);
    setErrors([]);
    setGeneralError('');

    try {
      const result = await productService.importProducts(file);

      if (result.success) {
        onSuccess();
        handleClose();
      }
    } catch (error: any) {
      console.error('Import error:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setGeneralError(error.response?.data?.message || 'Error al importar productos');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setErrors([]);
    setGeneralError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Importar Productos desde Excel"
      size="2xl"
    >
      <ModalBody className="p-6">
        <div className="space-y-6">
          {/* Step 1: Download Template */}
          <div className="p-4 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">1</span>
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-secondary-900 dark:text-white">Descargar plantilla</h4>
                <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
                  Descarga la plantilla Excel y llénala con tus productos. La plantilla incluye instrucciones y un ejemplo.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={handleDownloadTemplate}
                  disabled={isDownloading}
                  leftIcon={isDownloading ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="w-4 h-4" />}
                >
                  {isDownloading ? 'Descargando...' : 'Descargar Plantilla'}
                </Button>
              </div>
            </div>
          </div>

          {/* Step 2: Upload File */}
          <div className="p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">2</span>
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-secondary-900 dark:text-white">Subir archivo</h4>
                <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
                  Selecciona el archivo Excel con tus productos para importar.
                </p>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".xlsx,.xls"
                  className="hidden"
                />

                {!file ? (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-3 w-full p-4 border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <ArrowUpTrayIcon className="w-8 h-8 text-secondary-400" />
                      <span className="text-sm text-secondary-600 dark:text-secondary-400">
                        Haz clic para seleccionar archivo Excel
                      </span>
                      <span className="text-xs text-secondary-400">
                        Formatos: .xlsx, .xls (máx. 5MB)
                      </span>
                    </div>
                  </button>
                ) : (
                  <div className="mt-3 flex items-center gap-3 p-3 bg-secondary-100 dark:bg-secondary-700 rounded-lg">
                    <DocumentIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      onClick={handleRemoveFile}
                      className="p-1 rounded-full hover:bg-secondary-200 dark:hover:bg-secondary-600"
                    >
                      <XMarkIcon className="w-5 h-5 text-secondary-500" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* General Error */}
          {generalError && (
            <Alert variant="danger" title="Error">
              {generalError}
            </Alert>
          )}

          {/* Validation Errors */}
          {errors.length > 0 && (
            <Alert variant="danger" title="Errores de validación">
              <p className="mb-2">Se encontraron {errors.length} errores en el archivo:</p>
              <div className="max-h-40 overflow-y-auto border border-danger-200 dark:border-danger-800 rounded p-2 bg-danger-50/50 dark:bg-danger-900/20">
                <ul className="list-disc list-inside space-y-1">
                  {errors.map((error, index) => (
                    <li key={index} className="text-sm">
                      <span className="font-medium">Fila {error.row}</span>, {error.field}: {error.message}
                      {error.value && (
                        <span className="text-secondary-500 dark:text-secondary-400"> (valor: "{error.value}")</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </Alert>
          )}
        </div>
      </ModalBody>

      {/* Actions - En ModalFooter para que siempre estén visibles */}
      <ModalFooter>
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 text-sm font-medium text-secondary-700 dark:text-secondary-300 bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleImport}
          disabled={!file || isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          {isLoading ? (
            <>
              <Spinner size="sm" />
              <span>Importando...</span>
            </>
          ) : (
            <>
              <ArrowUpTrayIcon className="w-4 h-4" />
              <span>Importar Productos</span>
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
}
